/**
 * BI tab components for the redesigned Analytics hub (Phase 2D).
 * Overview KPIs, Attendance, Assessments, Learning, Departments, Risk.
 */
import { useEffect, useState } from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  CartesianGrid, PieChart, Pie, Cell, Legend,
} from 'recharts';
import {
  Building2, Boxes, CheckCircle2, Users, UserCheck, BadgeCheck, Percent, Timer,
} from 'lucide-react';
import { useApi } from '../../hooks/useApi';
import { Card } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from '../../components/ui/table';
import { buildQS } from './BIFilterBar';
import { TraineeFilterProvider } from '../trainees/TraineeFilterContext';
import DashboardTab from '../trainees/DashboardTab';

const BLUE = '#b68235';

function useBI(endpoint, filters) {
  const { get } = useApi();
  const [data, setData] = useState(null);
  useEffect(() => {
    let cancelled = false;
    setData(null);
    get(`/analytics/bi/${endpoint}${buildQS(filters)}`)
      .then(d => { if (!cancelled) setData(d); })
      .catch(() => {});
    return () => { cancelled = true; };
  }, [get, endpoint, filters]);
  return data;
}

function Kpi({ icon: Icon, label, value, testid }) {
  return (
    <Card className="p-4 flex items-center gap-3" data-testid={testid}>
      <div className="w-9 h-9 rounded-lg bg-[var(--lumyn-analytics,#b68235)]/10 text-[var(--lumyn-analytics,#b68235)] flex items-center justify-center shrink-0">
        <Icon className="w-4 h-4" />
      </div>
      <div>
        <p className="text-xl font-bold font-display leading-none tabular-nums">{value}</p>
        <p className="text-xs text-muted-foreground mt-1">{label}</p>
      </div>
    </Card>
  );
}

function ChartCard({ title, children, testid }) {
  return (
    <Card className="p-4" data-testid={testid}>
      <h4 className="font-display text-sm font-semibold mb-2">{title}</h4>
      {children}
    </Card>
  );
}

const Empty = () => <p className="text-sm text-muted-foreground py-6 text-center">No data in this range.</p>;

export function OverviewBITab({ filters }) {
  const d = useBI('overview', filters);
  if (!d) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-5" data-testid="bi-overview-tab">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <Kpi icon={Building2} label="Departments" value={d.total_departments} testid="kpi-departments" />
        <Kpi icon={Boxes} label="Active Batches" value={d.active_batches} testid="kpi-active-batches" />
        <Kpi icon={CheckCircle2} label="Completed Batches" value={d.completed_batches} testid="kpi-completed-batches" />
        <Kpi icon={Users} label="Total Trainees" value={d.total_trainees} testid="kpi-total-trainees" />
        <Kpi icon={UserCheck} label="Active Trainees" value={d.active_trainees} testid="kpi-active-trainees" />
        <Kpi icon={BadgeCheck} label="Certified" value={d.certified_trainees} testid="kpi-certified" />
        <Kpi icon={Percent} label="Completion Rate" value={`${d.completion_rate}%`} testid="kpi-completion" />
        <Kpi icon={Timer} label="Learning Hours" value={d.learning_hours} testid="kpi-learning-hours" />
      </div>
      {/* Relocated operational dashboard (formerly Trainee Management → Dashboard) */}
      <TraineeFilterProvider>
        <DashboardTab />
      </TraineeFilterProvider>
    </div>
  );
}

export function AttendanceBITab({ filters }) {
  const d = useBI('attendance', filters);
  if (!d) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="bi-attendance-tab">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Daily attendance trend" testid="att-daily-chart">
          {d.daily_trend.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={200}>
              <AreaChart data={d.daily_trend}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={x => x.slice(5)} fontSize={10} />
                <YAxis domain={[0, 100]} fontSize={10} />
                <Tooltip />
                <Area type="monotone" dataKey="pct" stroke={BLUE} fill={BLUE} fillOpacity={0.2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Weekly attendance" testid="att-weekly-chart">
          {d.weekly_trend.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={d.weekly_trend}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis dataKey="week" fontSize={10} />
                <YAxis domain={[0, 100]} fontSize={10} />
                <Tooltip />
                <Bar dataKey="pct" fill={BLUE} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Department-wise attendance" testid="att-dept-chart">
          {d.by_department.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={d.by_department} layout="vertical">
                <XAxis type="number" domain={[0, 100]} fontSize={10} />
                <YAxis type="category" dataKey="name" width={130} fontSize={10} />
                <Tooltip />
                <Bar dataKey="pct" fill="#c28d41" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Batch-wise attendance" testid="att-batch-chart">
          {d.by_batch.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={d.by_batch.slice(0, 12)} layout="vertical">
                <XAxis type="number" domain={[0, 100]} fontSize={10} />
                <YAxis type="category" dataKey="name" width={130} fontSize={10} />
                <Tooltip />
                <Bar dataKey="pct" fill="#7d7979" radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Below 80% attendance" testid="att-below80">
          {d.below_80.length === 0 ? <p className="text-sm text-muted-foreground py-4">Nobody below 80% — great!</p> : (
            <Table>
              <TableHeader><TableRow><TableHead>Trainee</TableHead><TableHead>Batch</TableHead><TableHead className="w-20">%</TableHead></TableRow></TableHeader>
              <TableBody>
                {d.below_80.map(r => (
                  <TableRow key={r.trainee_id}>
                    <TableCell className="font-medium">{r.name}</TableCell>
                    <TableCell className="text-xs">{r.batch}</TableCell>
                    <TableCell className="text-rose-600 font-semibold">{r.pct}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ChartCard>
        <ChartCard title="Leave status frequency" testid="att-leave-freq">
          {Object.keys(d.leave_status_counts).length === 0 ? <Empty /> : (
            <div className="flex flex-wrap gap-2 py-2">
              {Object.entries(d.leave_status_counts).map(([k, v]) => (
                <Badge key={k} variant="outline" className="text-sm px-3 py-1.5">
                  {k.replace('_', ' ')} · <span className="font-bold ml-1">{v}</span>
                </Badge>
              ))}
            </div>
          )}
        </ChartCard>
      </div>
    </div>
  );
}

export function AssessmentsBITab({ filters }) {
  const d = useBI('assessments', filters);
  if (!d) return <p className="text-sm text-muted-foreground">Loading…</p>;
  const pf = [
    { name: 'Passed', value: d.pass_fail.passed, color: '#10B981' },
    { name: 'Failed', value: d.pass_fail.failed, color: '#F43F5E' },
  ];
  return (
    <div className="space-y-4" data-testid="bi-assessments-tab">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ChartCard title="Average score trend" testid="ass-trend-chart">
          {d.score_trend.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={190}>
              <AreaChart data={d.score_trend}>
                <CartesianGrid stroke="#e2e8f0" strokeDasharray="3 3" />
                <XAxis dataKey="date" tickFormatter={x => x.slice(5)} fontSize={10} />
                <YAxis domain={[0, 100]} fontSize={10} />
                <Tooltip />
                <Area type="monotone" dataKey="avg" stroke={BLUE} fill={BLUE} fillOpacity={0.2} />
              </AreaChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Pass vs Fail" testid="ass-passfail-chart">
          {d.pass_fail.passed + d.pass_fail.failed === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={190}>
              <PieChart>
                <Pie data={pf} dataKey="value" innerRadius={45} outerRadius={70} paddingAngle={3}>
                  {pf.map(p => <Cell key={p.name} fill={p.color} />)}
                </Pie>
                <Legend />
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Score distribution" testid="ass-dist-chart">
          <ResponsiveContainer width="100%" height={190}>
            <BarChart data={d.distribution}>
              <XAxis dataKey="bucket" fontSize={9} />
              <YAxis allowDecimals={false} fontSize={10} />
              <Tooltip />
              <Bar dataKey="count" fill="#7d7979" radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Assessment comparison" testid="ass-comparison">
          {d.comparison.length === 0 ? <Empty /> : (
            <Table>
              <TableHeader><TableRow><TableHead>Assessment</TableHead><TableHead className="w-20">Attempts</TableHead><TableHead className="w-20">Avg %</TableHead><TableHead className="w-20">Pass %</TableHead></TableRow></TableHeader>
              <TableBody>
                {d.comparison.map(c => (
                  <TableRow key={c.assessment_id}>
                    <TableCell className="text-sm font-medium">{c.name}</TableCell>
                    <TableCell>{c.attempts}</TableCell>
                    <TableCell>{c.avg ?? '—'}</TableCell>
                    <TableCell>{c.pass_pct ?? '—'}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ChartCard>
        <div className="space-y-4">
          <ChartCard title="Highest scorers" testid="ass-top-scorers">
            {d.top_scorers.length === 0 ? <Empty /> : d.top_scorers.slice(0, 5).map((r, i) => (
              <div key={r.trainee_id} className="flex justify-between text-sm py-1.5 border-b last:border-0">
                <span>{i + 1}. {r.name}</span><span className="font-semibold text-emerald-700">{r.avg}%</span>
              </div>
            ))}
          </ChartCard>
          <ChartCard title="Lowest scorers" testid="ass-bottom-scorers">
            {d.bottom_scorers.length === 0 ? <Empty /> : d.bottom_scorers.slice(0, 5).map((r, i) => (
              <div key={r.trainee_id} className="flex justify-between text-sm py-1.5 border-b last:border-0">
                <span>{i + 1}. {r.name}</span><span className="font-semibold text-rose-600">{r.avg}%</span>
              </div>
            ))}
          </ChartCard>
        </div>
      </div>
    </div>
  );
}

export function LearningBITab({ filters }) {
  const d = useBI('learning', filters);
  if (!d) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="bi-learning-tab">
      <div className="grid grid-cols-2 sm:grid-cols-5 gap-3">
        <Kpi icon={Boxes} label="Assignments" value={d.assignments_total} testid="lkpi-assignments" />
        <Kpi icon={Users} label="Items Assigned" value={d.items_assigned} testid="lkpi-assigned" />
        <Kpi icon={CheckCircle2} label="Items Completed" value={d.items_completed} testid="lkpi-completed" />
        <Kpi icon={Percent} label="Avg Progress" value={`${d.avg_progress}%`} testid="lkpi-progress" />
        <Kpi icon={Timer} label="Overdue" value={d.overdue_assignments} testid="lkpi-overdue" />
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <ChartCard title="Learning time per trainee (minutes)" testid="learn-time-chart">
          {d.time_per_trainee.length === 0 ? <Empty /> : (
            <ResponsiveContainer width="100%" height={200}>
              <BarChart data={d.time_per_trainee} layout="vertical">
                <XAxis type="number" fontSize={10} />
                <YAxis type="category" dataKey="name" width={130} fontSize={10} />
                <Tooltip />
                <Bar dataKey="minutes" fill={BLUE} radius={[0, 4, 4, 0]} />
              </BarChart>
            </ResponsiveContainer>
          )}
        </ChartCard>
        <ChartCard title="Most viewed content" testid="learn-most-viewed">
          {d.most_viewed.length === 0 ? <Empty /> : (
            <Table>
              <TableHeader><TableRow><TableHead>Content</TableHead><TableHead className="w-16">Views</TableHead><TableHead className="w-20">Avg %</TableHead></TableRow></TableHeader>
              <TableBody>
                {d.most_viewed.map(r => (
                  <TableRow key={r.item_id}>
                    <TableCell className="text-sm">{r.title} <Badge variant="outline" className="ml-1 text-[10px]">{r.type}</Badge></TableCell>
                    <TableCell>{r.views}</TableCell>
                    <TableCell>{r.avg_pct}%</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </ChartCard>
      </div>
      <ChartCard title="Least completed content" testid="learn-least-completed">
        {d.least_completed.length === 0 ? <Empty /> : (
          <Table>
            <TableHeader><TableRow><TableHead>Content</TableHead><TableHead>Assignment</TableHead><TableHead className="w-24">Completed</TableHead><TableHead className="w-20">Avg %</TableHead></TableRow></TableHeader>
            <TableBody>
              {d.least_completed.map(r => (
                <TableRow key={r.item_id}>
                  <TableCell className="text-sm">{r.title}</TableCell>
                  <TableCell className="text-xs text-muted-foreground">{r.assignment}</TableCell>
                  <TableCell>{r.completed}/{r.views}</TableCell>
                  <TableCell className={r.avg_pct < 50 ? 'text-rose-600 font-semibold' : ''}>{r.avg_pct}%</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </ChartCard>
    </div>
  );
}

export function DepartmentsBITab({ filters }) {
  const d = useBI('departments', filters);
  if (!d) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <Card className="p-4" data-testid="bi-departments-tab">
      <h4 className="font-display text-sm font-semibold mb-2">Department comparison</h4>
      <Table data-testid="dept-comparison-table">
        <TableHeader>
          <TableRow>
            <TableHead>Department</TableHead><TableHead>Batches</TableHead>
            <TableHead>Trainees</TableHead><TableHead>Attendance</TableHead>
            <TableHead>Avg Score</TableHead><TableHead>Certified</TableHead>
            <TableHead>Completion</TableHead><TableHead>Dropout</TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {d.departments.map(r => (
            <TableRow key={r.department_id}>
              <TableCell className="font-medium">{r.name}</TableCell>
              <TableCell>{r.batches}</TableCell>
              <TableCell>{r.trainees}</TableCell>
              <TableCell>{r.attendance_pct != null ? `${r.attendance_pct}%` : '—'}</TableCell>
              <TableCell>{r.avg_assessment_score ?? '—'}</TableCell>
              <TableCell>{r.certified_pct != null ? `${r.certified_pct}%` : '—'}</TableCell>
              <TableCell>{r.completion_pct != null ? `${r.completion_pct}%` : '—'}</TableCell>
              <TableCell className={r.dropout_pct > 10 ? 'text-rose-600 font-semibold' : ''}>
                {r.dropout_pct != null ? `${r.dropout_pct}%` : '—'}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Card>
  );
}

const RISK_STYLE = {
  high: { dot: 'bg-rose-500', badge: 'bg-rose-100 text-rose-800', label: 'High' },
  medium: { dot: 'bg-amber-400', badge: 'bg-amber-100 text-amber-800', label: 'Medium' },
  low: { dot: 'bg-emerald-500', badge: 'bg-emerald-100 text-emerald-800', label: 'Low' },
};

export function RiskBITab({ filters }) {
  const d = useBI('risk', filters);
  if (!d) return <p className="text-sm text-muted-foreground">Loading…</p>;
  return (
    <div className="space-y-4" data-testid="bi-risk-tab">
      <div className="grid grid-cols-3 gap-3">
        {['high', 'medium', 'low'].map(k => (
          <Card key={k} className="p-4 flex items-center gap-3" data-testid={`risk-summary-${k}`}>
            <span className={`w-3 h-3 rounded-full ${RISK_STYLE[k].dot}`} />
            <div>
              <p className="text-xl font-bold font-display leading-none">{d.summary[k]}</p>
              <p className="text-xs text-muted-foreground mt-1">{RISK_STYLE[k].label} risk</p>
            </div>
          </Card>
        ))}
      </div>
      <Card data-testid="risk-table">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Trainee</TableHead><TableHead>Batch</TableHead>
              <TableHead className="w-24">Attendance</TableHead>
              <TableHead className="w-24">Assessment</TableHead>
              <TableHead className="w-20">Risk</TableHead>
              <TableHead>Flags</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {d.trainees.map(r => (
              <TableRow key={r.trainee_id} data-testid={`risk-row-${r.trainee_id}`}>
                <TableCell className="font-medium">{r.name}</TableCell>
                <TableCell className="text-xs">{r.batch}</TableCell>
                <TableCell>{r.attendance_pct != null ? `${r.attendance_pct}%` : '—'}</TableCell>
                <TableCell>{r.assessment_pct != null ? `${r.assessment_pct}%` : '—'}</TableCell>
                <TableCell>
                  <Badge className={`${RISK_STYLE[r.risk].badge} border-0`}>{RISK_STYLE[r.risk].label}</Badge>
                </TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1">
                    {r.flags.length === 0
                      ? <span className="text-xs text-muted-foreground">All good</span>
                      : r.flags.map((f, i) => <Badge key={i} variant="outline" className="text-[10px]">{f}</Badge>)}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
}
